// Deferred one-shot reminder that the user can author their OWN trigger.
//
// The tour teaches the double-click-on-the-timeline gesture in a single line
// (sayerTour.js, the post-branch step). A line heard once, while the user is busy
// absorbing six other things, is a line forgotten — so this re-offers it LATER, once,
// and only to someone who has settled in without ever using it.
//
// Eligibility is deliberately conservative, because an unsolicited message days after
// onboarding is the kind of thing that reads as nagging if it is even slightly wrong:
//   - tour COMPLETERS only (someone who hit Skip opted out of coaching; the caller
//     seeds this only on a completed tour, so a skipper has no record and never arms),
//   - at least NUDGE_MIN_FORECASTS of the user's own forecasts have landed,
//   - at least NUDGE_MIN_AGE_MS has elapsed since the tour ended.
//
// The age floor is not redundant with the forecast count: three forecasts can happen
// in ten minutes on day one, and a reminder arriving while the tour is still fresh
// teaches nothing that the tour did not just say.
//
// It fires at most once EVER, and is permanently suppressed the moment the user
// authors a trigger by EITHER route — the timeline popover or the chat `add_event`
// intent. Both funnel through handleCustomEventBranch, which is where the suppression
// call lives, so there is one choke point rather than two that can drift.
//
// SCOPE: localStorage, so this is per-BROWSER, not per-account. A user on a second
// device starts a fresh count and could in principle see the reminder twice. Fixing
// that properly means server-side prefs; that surface is not worth it for a one-shot
// tip, and seeing a tip twice is a much smaller harm than the tip never arriving.

/** localStorage key holding the nudge record. */
export const CUSTOM_TRIGGER_NUDGE_KEY = "sooth-custom-trigger-nudge";

/** Own forecasts that must land before the reminder is eligible. */
export const NUDGE_MIN_FORECASTS = 2;

/** Time that must elapse after the tour before the reminder is eligible (1 day). */
export const NUDGE_MIN_AGE_MS = 86400000;

/**
 * The store every entry point defaults to. Resolved lazily per call rather than at
 * module load: this module is imported by `commands/` code that also runs under the
 * node test environment, where `localStorage` is not defined at all.
 *
 * @returns {Storage|null}
 */
function defaultStore() {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null; // some privacy modes throw on mere access
  }
}

/**
 * Read the stored record, or null when absent/unparseable.
 *
 * @param {Storage|null} [store] injectable for tests
 * @returns {{n: number, since: number, done: string|null}|null}
 */
export function readNudgeState(store = defaultStore()) {
  try {
    const raw = store?.getItem(CUSTOM_TRIGGER_NUDGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s || typeof s !== "object") return null;
    return {
      n: Number.isFinite(s.n) ? s.n : 0,
      since: Number.isFinite(s.since) ? s.since : 0,
      done: typeof s.done === "string" ? s.done : null,
    };
  } catch {
    return null;
  }
}

/**
 * Persist a record (best-effort — private mode / quota is a silent no-op).
 *
 * @param {{n: number, since: number, done: string|null}} s
 * @param {Storage|null} [store] injectable for tests
 */
export function writeNudgeState(s, store = defaultStore()) {
  try {
    store?.setItem(CUSTOM_TRIGGER_NUDGE_KEY, JSON.stringify(s));
  } catch {
    /* private mode / quota — best-effort */
  }
}

/**
 * Arm the reminder. Called once, when the tour COMPLETES.
 *
 * Seeds only when no record exists, so replaying the tour neither resets a user who
 * already saw the reminder nor pushes the age floor back out for one who is partway
 * to it. A replay means they re-heard the line, which is the thing the reminder
 * exists to substitute for.
 *
 * @param {number} [now] epoch ms (injectable for tests)
 * @param {Storage|null} [store] injectable for tests
 */
export function startCustomTriggerNudge(now = Date.now(), store = defaultStore()) {
  if (readNudgeState(store)) return;
  writeNudgeState({ n: 0, since: now, done: null }, store);
}

/**
 * Permanently suppress the reminder — the user authored a trigger themselves.
 *
 * Writes a closed record even for a user who never had one (a skipper, or someone who
 * predates this feature), so a later tour completion cannot re-arm them: they have
 * demonstrably found the gesture, and `startCustomTriggerNudge` seeds only when the
 * record is absent.
 *
 * @param {Storage|null} [store] injectable for tests
 */
export function markCustomTriggerUsed(store = defaultStore()) {
  const s = readNudgeState(store) || { n: 0, since: 0, done: null };
  if (s.done) return;
  writeNudgeState({ ...s, done: "used" }, store);
}

/**
 * Whether a record is eligible for the reminder right now. PURE — takes the state
 * rather than reading storage, so the eligibility rules are testable in isolation.
 *
 * @param {{n: number, since: number, done: string|null}|null} s
 * @param {number} now epoch ms
 * @returns {boolean}
 */
export function shouldNudge(s, now) {
  if (!s || s.done) return false;
  if (!s.since) return false; // never armed — no tour completion to measure from
  if (s.n < NUDGE_MIN_FORECASTS) return false;
  return now - s.since >= NUDGE_MIN_AGE_MS;
}

/**
 * Count one landed forecast of the user's own. No-op once the record is closed, and
 * for a user who was never armed (no tour completion => nothing to remind them of).
 *
 * @param {Storage|null} [store] injectable for tests
 */
export function recordForecastLanded(store = defaultStore()) {
  const s = readNudgeState(store);
  if (!s || s.done) return;
  writeNudgeState({ ...s, n: s.n + 1 }, store);
}

/**
 * Claim the one-shot reminder: true exactly once, for the first eligible check.
 *
 * Marks the record closed BEFORE returning true, so a double-invocation (two runs
 * completing together) cannot post the message twice.
 *
 * @param {number} [now] epoch ms (injectable for tests)
 * @param {Storage|null} [store] injectable for tests
 * @returns {boolean} whether the caller should post the reminder
 */
export function claimNudge(now = Date.now(), store = defaultStore()) {
  const s = readNudgeState(store);
  if (!shouldNudge(s, now)) return false;
  writeNudgeState({ ...s, done: "nudged" }, store);
  return true;
}
